/**
 * Confetti — Phase 5 / Iter75.
 *
 * Thin wrapper around `canvas-confetti` exposing a single
 * `fire({ origin, scalar, colors })` call with brand-coloured defaults.
 * Reduced-motion users get nothing — the import still tree-shakes
 * to ~6kb so the perf hit is trivial when unused.
 *
 * Usage:
 *   import { fireConfetti } from "@/lib/confetti";
 *   fireConfetti();                       // burst from screen centre
 *   fireConfetti({ origin: { y: 0.3 } }); // upper third
 */
import confetti from "canvas-confetti";

const OAK_COLORS = ["#00FF9D", "#0044FF", "#5D3FD3", "#FFB067", "#FF6BAA"];

function prefersReducedMotion() {
  if (typeof window === "undefined") return false;
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  } catch {
    return false;
  }
}

export function fireConfetti({
  origin = { x: 0.5, y: 0.5 },
  particleCount = 90,
  spread = 70,
  scalar = 1,
  colors = OAK_COLORS,
  startVelocity = 38,
} = {}) {
  if (prefersReducedMotion()) return;
  try {
    confetti({
      particleCount,
      spread,
      origin,
      scalar,
      colors,
      startVelocity,
      ticks: 180,
      zIndex: 200,
      disableForReducedMotion: true,
    });
  } catch {
    /* swallow — confetti is decorative, never fatal */
  }
}

/** Two-sided cannon — used for big wins (level-up, full deck pass). */
export function fireConfettiCannons() {
  if (prefersReducedMotion()) return;
  const defaults = {
    spread: 55,
    ticks: 200,
    gravity: 0.9,
    colors: OAK_COLORS,
    zIndex: 200,
  };
  try {
    confetti({ ...defaults, particleCount: 60, origin: { x: 0, y: 0.7 }, angle: 60 });
    confetti({ ...defaults, particleCount: 60, origin: { x: 1, y: 0.7 }, angle: 120 });
  } catch {
    /* noop */
  }
}
